import { Component, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import {
  PlanningAssignment,
  ResourceCategory,
  ResourceEmployee,
  ResourceExternalEvent,
  ResourceMyCalendarContext
} from '../../../Models/resource.models';
import { ResourceAbsencePlanning } from '../../../Models/resource-modules.models';
import { ResourceApiService } from '../../../Services/resource-api.service';
import { ResourceAbsenceApiService } from '../../../Services/resource-modules-api.service';
import {
  ResourceHiddenExternalEventsDialogComponent,
  ResourceHiddenExternalEventsDialogData,
  ResourceHiddenExternalEventsDialogResult
} from '../planning/hidden-external-events-dialog/resource-hidden-external-events-dialog.component';
import {
  MyCalendarItem, MyCalendarSlot, addDays, compareItems, fromAbsence, fromAssignment, fromExternal,
  parseDateKey, startOfWeek, toDateKey
} from './my-calendar.models';

/** Gleiche Ansichten wie die Teamplanung. */
type CalendarView = 'week' | 'month' | 'quarter' | 'year';

/** Ein Monatsraster (Monat, Quartal und Jahr bestehen aus 1, 3 bzw. 12 Monaten). */
interface MonthBlock {
  start: string;
  days: string[];
  label: string;
}

const VIEW_STORAGE_KEY = 'rv2.myCalendar.view';
const MONTHS_PER_VIEW: Record<Exclude<CalendarView, 'week'>, number> = { month: 1, quarter: 3, year: 12 };

/**
 * „Mein Kalender“: interne Planungstermine, eigene Outlook-Vorschläge und genehmigte
 * Abwesenheiten in Woche, Monat, Quartal oder Jahr (Bedienleiste wie die Teamplanung).
 *
 * - Outlook ist nur Quelle: Vorschläge können übernommen (→ interner Termin) oder
 *   ausgeblendet werden; Outlook selbst wird nie verändert.
 * - Eigene Termine werden mit dem Planungseditor der Teamplanung angelegt und
 *   bearbeitet – ausschließlich über die persönlichen Endpunkte (nur eigene Daten).
 * - Abwesenheiten sind schreibgeschützt (Änderungen über „Abwesenheiten“).
 */
@Component({
  selector: 'app-resource-my-calendar',
  templateUrl: './resource-my-calendar.component.html',
  styleUrls: ['../shared/resource-page-toolbar.css', './resource-my-calendar.component.css']
})
export class ResourceMyCalendarComponent implements OnInit {
  view: CalendarView = 'week';
  readonly today = toDateKey(new Date());
  anchor = this.today;

  context: ResourceMyCalendarContext | null = null;
  employees: ResourceEmployee[] = [];
  assignments: PlanningAssignment[] = [];
  private externalEvents: ResourceExternalEvent[] = [];
  private absences: ResourceAbsencePlanning[] = [];

  /** Alle Einträge des geladenen Zeitraums (für alle Ansichten dieselben). */
  visibleItems: MyCalendarItem[] = [];
  /** Monatsraster der Monats-, Quartals- bzw. Jahresansicht. */
  monthBlocks: MonthBlock[] = [];

  loading = false;
  saving = false;
  error = '';
  outlookWarning = '';
  hiddenCount = 0;

  editorOpen = false;
  editingAssignment: PlanningAssignment | null = null;
  detail: MyCalendarItem | null = null;

  private loadSequence = 0;

  constructor(
    private api: ResourceApiService,
    private absenceApi: ResourceAbsenceApiService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {
    try {
      const stored = localStorage.getItem(VIEW_STORAGE_KEY) as CalendarView | null;
      if (stored === 'week' || stored === 'month' || stored === 'quarter' || stored === 'year') this.view = stored;
    } catch { /* ohne Speicher: Standardansicht */ }
  }

  ngOnInit(): void {
    this.loading = true;
    this.api.getMyCalendarContext().subscribe({
      next: context => {
        this.context = context;
        this.employees = [this.api.toEmployeeFromDto(context.employee)];
        if (context.hasCalendarFeed) this.loadHiddenCount();
        this.load();
      },
      error: () => {
        this.loading = false;
        this.error = 'Dein Kalender konnte nicht geladen werden.';
      }
    });
  }

  get userId(): string { return this.context?.employee.id ?? ''; }
  get hasCalendarFeed(): boolean { return !!this.context?.hasCalendarFeed; }

  // ---- Zeitraum -------------------------------------------------------------------

  /** Erster Monat der Monats-, Quartals- bzw. Jahresansicht. */
  private get firstMonth(): string {
    const date = parseDateKey(`${this.anchor.substring(0, 7)}-01`);
    if (this.view === 'quarter') date.setMonth(Math.floor(date.getMonth() / 3) * 3);
    if (this.view === 'year') date.setMonth(0);
    return toDateKey(date);
  }

  private monthStarts(): string[] {
    if (this.view === 'week') return [];
    const first = parseDateKey(this.firstMonth);
    return Array.from({ length: MONTHS_PER_VIEW[this.view] }, (_, i) =>
      toDateKey(new Date(first.getFullYear(), first.getMonth() + i, 1)));
  }

  /** Geladener Zeitraum (alle sichtbaren Tage der Raster); to ist exklusiv. */
  get range(): { from: string; to: string } {
    if (this.view === 'week') {
      const from = startOfWeek(this.anchor);
      return { from, to: addDays(from, 7) };
    }
    const starts = this.monthStarts();
    return { from: startOfWeek(starts[0]), to: addDays(startOfWeek(starts[starts.length - 1]), 42) };
  }

  get weekDays(): string[] {
    const from = startOfWeek(this.anchor);
    return Array.from({ length: 7 }, (_, i) => addDays(from, i));
  }

  private buildMonthBlocks(): void {
    this.monthBlocks = this.monthStarts().map(start => {
      const from = startOfWeek(start);
      return {
        start,
        days: Array.from({ length: 42 }, (_, i) => addDays(from, i)),
        label: parseDateKey(start).toLocaleDateString('de-AT', { month: 'long' })
      };
    });
  }

  get periodLabel(): string {
    if (this.view === 'week') {
      const days = this.weekDays;
      const first = parseDateKey(days[0]);
      const last = parseDateKey(days[6]);
      const kw = this.isoWeek(first);
      return `KW ${kw} · ${first.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit' })} – ` +
        last.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
    const first = parseDateKey(this.firstMonth);
    if (this.view === 'quarter') return `Q${Math.floor(first.getMonth() / 3) + 1} ${first.getFullYear()}`;
    if (this.view === 'year') return `${first.getFullYear()}`;
    return first.toLocaleDateString('de-AT', { month: 'long', year: 'numeric' });
  }

  get periodNoun(): string {
    return { week: 'Woche', month: 'Monat', quarter: 'Quartal', year: 'Jahr' }[this.view];
  }

  setView(view: CalendarView): void {
    if (view === this.view) return;
    this.view = view;
    try { localStorage.setItem(VIEW_STORAGE_KEY, view); } catch { /* ignorieren */ }
    this.load();
  }

  move(direction: -1 | 1): void {
    if (this.view === 'week') {
      this.anchor = addDays(this.anchor, 7 * direction);
    } else {
      const date = parseDateKey(this.firstMonth);
      date.setMonth(date.getMonth() + direction * MONTHS_PER_VIEW[this.view]);
      this.anchor = toDateKey(date);
    }
    this.load();
  }

  goToday(): void {
    this.anchor = this.today;
    this.load();
  }

  /** Aus Monat, Quartal oder Jahr: Woche des Tages öffnen. */
  openDay(day: string): void {
    this.anchor = day;
    this.setView('week');
  }

  // ---- Laden ----------------------------------------------------------------------

  load(): void {
    if (!this.context) return;
    const { from, to } = this.range;
    const sequence = ++this.loadSequence;
    this.buildMonthBlocks();
    this.loading = true;
    this.error = '';
    this.outlookWarning = '';

    forkJoin({
      assignments: this.api.getMyAssignments(from, to),
      external: this.hasCalendarFeed
        ? this.api.getMyExternalEvents(from, to).pipe(catchError(() => {
          this.outlookWarning = 'Outlook-Termine konnten nicht geladen werden.';
          return of([] as ResourceExternalEvent[]);
        }))
        : of([] as ResourceExternalEvent[]),
      // Ausdrücklich nur die eigene Benutzer-ID (das Backend prüft zusätzlich).
      absences: this.absenceApi.getPlanning(`${from}T00:00:00`, `${to}T00:00:00`, [this.userId])
        .pipe(catchError(() => of([] as ResourceAbsencePlanning[])))
    }).subscribe({
      next: result => {
        if (sequence !== this.loadSequence) return;
        this.assignments = result.assignments;
        this.externalEvents = result.external;
        this.absences = result.absences.filter(a => a.userId === this.userId);
        this.loading = false;
        this.rebuild();
      },
      error: (err: HttpErrorResponse) => {
        if (sequence !== this.loadSequence) return;
        this.loading = false;
        this.error = 'Deine Termine konnten nicht geladen werden: ' + this.errorText(err);
      }
    });
  }

  /** Termine, Outlook-Vorschläge und Abwesenheiten werden immer gemeinsam angezeigt. */
  private rebuild(): void {
    const { from, to } = this.range;
    const last = addDays(to, -1);
    this.visibleItems = [
      ...this.assignments.map(fromAssignment),
      ...this.externalEvents.map(fromExternal),
      ...this.absences.map(fromAbsence)
    ].filter(i => i.endDate >= from && i.startDate <= last).sort(compareItems);
  }

  private loadHiddenCount(): void {
    this.api.getMyHiddenExternalEvents().subscribe({
      next: hidden => this.hiddenCount = hidden.length,
      error: () => { }
    });
  }

  // ---- Interaktion ---------------------------------------------------------------------

  onItemClick(item: MyCalendarItem): void {
    if (this.saving) return;
    if (item.kind === 'assignment' && item.editable && item.assignment) {
      this.openEditor(this.copyAssignment(item.assignment));
      return;
    }
    this.detail = item;
  }

  onSlotSelect(slot: MyCalendarSlot): void {
    if (this.saving || this.editorOpen) return;
    this.openEditor(this.newAssignment(slot));
  }

  addAppointment(): void {
    const { from, to } = this.range;
    const day = this.today >= from && this.today < to ? this.today : from;
    this.openEditor(this.newAssignment({ startDate: day, startTime: '08:00', endDate: day, endTime: '09:00' }));
  }

  closeDetail(): void {
    this.detail = null;
  }

  /** Outlook-Vorschlag mit dem Planungseditor übernehmen (Kategorie bewusst auswählen). */
  adopt(item: MyCalendarItem): void {
    const event = item.external;
    if (!event) return;
    this.detail = null;
    this.openEditor({
      ...this.newAssignment({
        startDate: item.startDate, startTime: item.startTime, endDate: item.endDate, endTime: item.endTime
      }),
      title: item.title,
      categoryId: null,
      category: '',
      sourceExternalUid: event.uid,
      sourceExternalUserId: event.userId,
      sourceExternalStart: event.start
    });
  }

  /** Vorschlag dauerhaft ausblenden – der Termin bleibt in Outlook unverändert. */
  hide(item: MyCalendarItem): void {
    const event = item.external;
    if (!event || this.saving) return;
    this.saving = true;
    this.api.hideMyExternalEvent({
      userId: this.userId, uid: event.uid, start: event.start, end: event.end, title: event.title, allDay: event.allDay
    }).subscribe({
      next: hidden => {
        this.saving = false;
        this.detail = null;
        this.externalEvents = this.externalEvents.filter(e => e !== event);
        this.hiddenCount += 1;
        this.rebuild();
        this.snackBar.open('Outlook-Vorschlag ausgeblendet.', 'Rückgängig', { duration: 6000 })
          .onAction().subscribe(() => this.unhide(hidden.id));
      },
      error: (err: HttpErrorResponse) => {
        this.saving = false;
        this.snackBar.open('Ausblenden fehlgeschlagen: ' + this.errorText(err), 'OK', { duration: 6000 });
      }
    });
  }

  private unhide(id: number): void {
    this.api.unhideMyExternalEvent(id).subscribe({
      next: () => { this.hiddenCount = Math.max(0, this.hiddenCount - 1); this.load(); },
      error: () => this.load()
    });
  }

  openHiddenDialog(): void {
    const data: ResourceHiddenExternalEventsDialogData = { employees: this.employees, personal: true };
    this.dialog.open<
      ResourceHiddenExternalEventsDialogComponent,
      ResourceHiddenExternalEventsDialogData,
      ResourceHiddenExternalEventsDialogResult
    >(ResourceHiddenExternalEventsDialogComponent, {
      data, width: '600px', maxWidth: '95vw', maxHeight: '85vh', autoFocus: false, disableClose: true
    }).afterClosed().subscribe(result => {
      if (result) this.hiddenCount = result.remainingCount;
      if (result?.changed) this.load();
    });
  }

  // ---- Editor ---------------------------------------------------------------------------

  /** Kategorien im Editor: erlaubte Kategorien und die bisherige Kategorie eines bestehenden Termins. */
  get editorCategories(): ResourceCategory[] {
    const categories = this.context?.categories ?? [];
    const current = this.editingAssignment;
    if (current?.id && current.categoryId && !categories.some(c => c.id === current.categoryId)) {
      return [...categories, {
        id: current.categoryId, name: current.category, color: current.color,
        countsAsAbsence: false, excludeFromCapacity: false, sortOrder: 9999
      }];
    }
    return categories;
  }

  private openEditor(assignment: PlanningAssignment): void {
    this.editingAssignment = assignment;
    this.editorOpen = true;
  }

  closeEditor(): void {
    if (this.saving) return;
    this.editorOpen = false;
    this.editingAssignment = null;
  }

  saveAssignment(assignment: PlanningAssignment): void {
    if (this.saving) return;
    const adopted = !assignment.id && !!assignment.sourceExternalUid;
    this.saving = true;
    this.api.saveMyAssignment({
      ...assignment,
      // Teilnehmer ist immer der angemeldete Benutzer (das Backend erzwingt das ebenfalls).
      participants: [{
        employeeId: this.userId,
        utilizationPercent: assignment.participants.find(p => p.employeeId === this.userId)?.utilizationPercent ?? 100
      }],
      sendOutlookInvitation: false
    }).subscribe({
      next: () => {
        this.saving = false;
        this.editorOpen = false;
        this.editingAssignment = null;
        this.snackBar.open(adopted ? 'Outlook-Termin in die Planung übernommen.' : 'Termin gespeichert.',
          undefined, { duration: 3000 });
        this.load();
      },
      error: (err: HttpErrorResponse) => {
        this.saving = false;
        this.snackBar.open('Speichern fehlgeschlagen: ' + this.errorText(err), 'OK', { duration: 6000 });
        // Bereits übernommen (z. B. in einem anderen Fenster): Anzeige aktualisieren.
        if (adopted && err.status === 400) this.load();
      }
    });
  }

  deleteAssignment(id: number): void {
    if (this.saving) return;
    const fromOutlook = !!this.editingAssignment?.sourceExternalUid;
    this.saving = true;
    this.api.deleteMyAssignment(id).subscribe({
      next: () => {
        this.saving = false;
        this.editorOpen = false;
        this.editingAssignment = null;
        this.snackBar.open(fromOutlook
          ? 'Termin gelöscht. Der Outlook-Termin bleibt unverändert und wird nicht erneut vorgeschlagen.'
          : 'Termin gelöscht.', 'OK', { duration: 5000 });
        if (fromOutlook) this.hiddenCount += 1;
        this.load();
      },
      error: (err: HttpErrorResponse) => {
        this.saving = false;
        this.snackBar.open('Löschen fehlgeschlagen: ' + this.errorText(err), 'OK', { duration: 6000 });
      }
    });
  }

  private newAssignment(slot: MyCalendarSlot): PlanningAssignment {
    const category = this.context?.categories[0] ?? null;
    return {
      id: 0,
      teamId: null,
      title: '',
      startDate: slot.startDate,
      endDate: slot.endDate,
      startTime: slot.startTime,
      endTime: slot.endTime,
      categoryId: category?.id ?? null,
      category: category?.name ?? '',
      color: category?.color ?? '#2563eb',
      participants: [{ employeeId: this.userId, utilizationPercent: 100 }],
      weekendDates: [],
      weekendDays: [],
      customerId: null,
      plantId: null,
      location: '',
      sendOutlookInvitation: false
    };
  }

  private copyAssignment(a: PlanningAssignment): PlanningAssignment {
    return {
      ...a,
      participants: a.participants.map(p => ({ ...p })),
      weekendDates: [...a.weekendDates],
      weekendDays: (a.weekendDays ?? []).map(d => ({ date: d.date, hoursByEmployee: { ...d.hoursByEmployee } }))
    };
  }

  // ---- Anzeige-Hilfen --------------------------------------------------------------------

  kindLabel(item: MyCalendarItem): string {
    switch (item.kind) {
      case 'outlook': return 'Outlook-Vorschlag';
      case 'absence': return 'Genehmigte Abwesenheit';
      default: return item.editable ? 'Eigener Planungstermin' : 'Zugewiesener Planungstermin';
    }
  }

  formatRange(item: MyCalendarItem): string {
    const fmt = (d: string) => parseDateKey(d).toLocaleDateString('de-AT', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
    return item.startDate === item.endDate
      ? `${fmt(item.startDate)}, ${item.startTime} – ${item.endTime} Uhr`
      : `${fmt(item.startDate)} ${item.startTime} – ${fmt(item.endDate)} ${item.endTime} Uhr`;
  }


  trackMonth(_i: number, block: MonthBlock): string { return block.start; }

  private isoWeek(date: Date): number {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const day = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - day);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  }

  private errorText(err: HttpErrorResponse): string {
    if (typeof err?.error === 'string' && err.error.trim()) return err.error;
    if (err?.status === 403) return 'Keine Berechtigung.';
    if (err?.status === 0) return 'Server nicht erreichbar.';
    return err?.error?.title || err?.message || 'Unbekannter Fehler.';
  }
}
